import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { PurchaseProps, PurchaseOptionsProps } from "../../types/purchase";

export interface PurchaseState {
  purchases: PurchaseProps[];
  totalPurchases: number;
  currentPurchase: PurchaseProps | null;
  currentPurchaseLoading: boolean;
  partyOptions: PurchaseOptionsProps[];
  itemOptions: PurchaseOptionsProps[];
  loading: boolean;
  error: string | null;
}

const initialState: PurchaseState = {
  purchases: [],
  totalPurchases: 0,
  currentPurchase: null,
  currentPurchaseLoading: false,
  partyOptions: [],
  itemOptions: [],
  loading: false,
  error: null,
};

type FetchPurchaseListDataProps = {
  page: number;
  size?: number;
  fromDate?: string | null;
  toDate?: string | null;
  search?: string | null;
  party_id?: number | null;
};

export const fetchPurchaseListData = createAsyncThunk(
  "fetchPurchaseListData",
  async (req: FetchPurchaseListDataProps) => {
    let response: any;
    await GET(`/v1/admin/purchases`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchPurchaseDetail = createAsyncThunk(
  "fetchPurchaseDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/purchases/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchPurchasePartiesOptions = createAsyncThunk(
  "fetchPurchasePartiesOptions",
  async () => {
    let response: any;
    await GET(`/v1/admin/purchase-parties`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchPurchaseItemsOptions = createAsyncThunk(
  "fetchPurchaseItemsOptions",
  async () => {
    let response: any;
    await GET(`/v1/admin/purchase-items`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const purchaseSlice = createSlice({
  name: "purchase",
  initialState,
  reducers: {
    clearCurrentPurchase: (state) => {
      state.currentPurchase = null;
      state.currentPurchaseLoading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch purchases list
    builder.addCase(fetchPurchaseListData.pending, (state) => {
      state.purchases = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchPurchaseListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.purchases = action.payload.data.data.records;
          state.totalPurchases = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.purchases = [];
          state.error =
            action?.payload?.data?.message ||
            "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
        }
      }
    );
    builder.addCase(
      fetchPurchaseListData.rejected,
      (state, action: any) => {
        state.purchases = [];
        state.loading = false;
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    );

    // Fetch purchase detail
    builder.addCase(fetchPurchaseDetail.pending, (state) => {
      state.currentPurchaseLoading = true;
      state.currentPurchase = null;
    });
    builder.addCase(
      fetchPurchaseDetail.fulfilled,
      (state, action: any) => {
        state.currentPurchaseLoading = false;
        if (action.payload.data.status === "success") {
          state.currentPurchase = action.payload.data.data;
        }
      }
    );
    builder.addCase(fetchPurchaseDetail.rejected, (state) => {
      state.currentPurchaseLoading = false;
      state.currentPurchase = null;
    });

    // Fetch party options
    builder.addCase(
      fetchPurchasePartiesOptions.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.partyOptions = action.payload.data.data.records.map(
            (party: any) => ({
              value: party.id,
              label: party.firm_name + (party.name ? " - " + party.name : "") + (party.phone ? " (" + party.phone + ")" : ""),
            })
          );
        }
      }
    );

    // Fetch item options
    builder.addCase(
      fetchPurchaseItemsOptions.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.itemOptions = action.payload.data.data.records.map(
            (item: any) => ({
              value: item.id,
              label: item.name,
            })
          );
        }
      }
    );
  },
});

export const { clearCurrentPurchase } = purchaseSlice.actions;

export const getPurchases = (state: RootState) => state.purchase.purchases;
export const getTotalPurchases = (state: RootState) =>
  state.purchase.totalPurchases;
export const getCurrentPurchase = (state: RootState) =>
  state.purchase.currentPurchase;
export const getCurrentPurchaseLoading = (state: RootState) =>
  state.purchase.currentPurchaseLoading;
export const getPartyOptions = (state: RootState) =>
  state.purchase.partyOptions;
export const getItemOptions = (state: RootState) => state.purchase.itemOptions;

export default purchaseSlice.reducer;
